import { useState } from 'react';
import { Center, Loader } from '@mantine/core';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { SavedSignaturePicker } from '@dealstack/frontend/shared/components/Signature/SavedSignaturePicker/SavedSignaturePicker';
import { SignatureEditor } from '@dealstack/frontend/shared/components/Signature/SignatureEditor/SignatureEditor';
import { useSavedSignature } from '@dealstack/frontend/shared/components/Signature/hooks/useSavedSignature';
import { useSaveUserSignature } from '@dealstack/frontend/shared/components/Signature/hooks/useSaveUserSignature';

interface Props {
	onConfirm: (imageUrl: string) => void;
	onClose: () => void;
}

export const AddSignatureModalBody = ({ onConfirm, onClose }: Props) => {
	const tCommonUi = useTranslationCommonUi();
	const saved = useSavedSignature();
	const saveMutation = useSaveUserSignature();
	const [showEditor, setShowEditor] = useState(false);

	const showPicker = saved.isPresent && !showEditor;

	const handleUseSaved = () => {
		if (saved.savedSignatureDataUrl) {
			onConfirm(saved.savedSignatureDataUrl);
			onClose();
		}
	};

	const handleConfirmFromEditor = async (dataUrl: string) => {
		try {
			await saveMutation.mutateAsync(dataUrl);
		} catch {
			showToastV2({
				state: 'error',
				title: tCommonUi('eid_easy_signing.signature_save_failed_title', {
					defaultValue: "Couldn't save signature",
				}),
				message: tCommonUi('eid_easy_signing.signature_save_failed_description', {
					defaultValue:
						"We couldn't save your signature for next time, but you can still use it now.",
				}),
			});
		}
		onConfirm(dataUrl);
		onClose();
	};

	if (saved.isLoading) {
		return (
			<Center mih={200}>
				<Loader />
			</Center>
		);
	}

	if (showPicker) {
		return (
			<SavedSignaturePicker
				savedSignatureDataUrl={saved.savedSignatureDataUrl ?? ''}
				onUseSaved={handleUseSaved}
				onCreateNew={() => setShowEditor(true)}
			/>
		);
	}

	return (
		<SignatureEditor
			confirmLabel={tCommonUi('add_signature_modal.confirm', { defaultValue: 'Confirm' })}
			onConfirm={handleConfirmFromEditor}
			isBusy={saveMutation.isPending}
		/>
	);
};
